import { useMemo } from "react";

import { useFormDynamicFields } from "../../hooks/useForm/useFormDynamicFields";
import type { FormStore } from "../../store/types";
import type {
  AttributeCollectionId,
  AttributeCollection as AttributeCollectionType,
} from "../../types";

import type { DynamicGroupData } from "./DynamicGroupRenderer";

/**
 * コレクションデータと動的グループを処理するカスタムフック
 */
export function useCollectionData(
  collectionId: AttributeCollectionId,
  store: FormStore,
) {
  const { getDynamicFieldGroup } = useFormDynamicFields({ store });

  const collectionData = useMemo<AttributeCollectionType | undefined>(
    () => store.getCollection(collectionId),
    [store, collectionId],
  );

  // 動的グループの状態を管理
  const dynamicGroups = useMemo<DynamicGroupData[]>(() => {
    if (!collectionData?.attributeGroupSettings) return [];

    return collectionData.attributeGroupSettings
      .filter((group) => group.isDynamic)
      .map((group) => {
        const { fieldValues } = getDynamicFieldGroup(collectionId, group.id);

        // fieldValuesをソートし、表示用のインデックスを追加
        return {
          group,
          fieldValues: fieldValues
            .sort((a, b) => a.groupIndex - b.groupIndex)
            .map((value, index) => ({
              ...value,
              displayIndex: index, // 連続した表示インデックス
            })),
        };
      });
  }, [collectionId, collectionData, getDynamicFieldGroup]);

  return {
    collectionData,
    dynamicGroups,
  };
}
